import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConsentProvider } from "./context/ConsentContext";
import { ConsentBanner } from "./components/ConsentBanner";
import { pageview } from "./lib/analytics";

// Route-level code splitting: pages load in their own chunks on demand.
const HomePage = lazy(() => import("./pages/HomePage"));
const AppsPage = lazy(() => import("./pages/AppsPage"));
const MoodyMealsPage = lazy(() => import("./pages/MoodyMealsPage"));
const NotesFromTheNestPage = lazy(() => import("./pages/NotesFromTheNestPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PrintablePage = lazy(() => import("./pages/PrintablePage"));
const AnniversaryRescueWifePage = lazy(() => import("./pages/AnniversaryRescueWifePage"));
const AnniversaryRescueHusbandPage = lazy(() => import("./pages/AnniversaryRescueHusbandPage"));
const AnniversaryBundlePage = lazy(() => import("./pages/AnniversaryBundlePage"));
const MoreThanMoneyPage = lazy(() => import("./pages/MoreThanMoneyPage"));
const BabyFirstYearBooksPage = lazy(() => import("./pages/BabyFirstYearBooksPage"));
const BabySurvivalBookPage = lazy(() => import("./pages/BabySurvivalBookPage"));
const BirthdayOrganizerPage = lazy(() => import("./pages/BirthdayOrganizerPage"));
const ChristmasGiftTrackerPage = lazy(() => import("./pages/ChristmasGiftTrackerPage"));
const ChristmasManagedPage = lazy(() => import("./pages/ChristmasManagedPage"));
const CoverYourButtPage = lazy(() => import("./pages/CoverYourButtPage"));
const CruisePlannerPage = lazy(() => import("./pages/CruisePlannerPage"));
const BetweenHereAndHomePage = lazy(() => import("./pages/BetweenHereAndHomePage"));
const WhatsForDinnerPage = lazy(() => import("./pages/WhatsForDinnerPage"));
const EmergencyBinderPage = lazy(() => import("./pages/EmergencyBinderPage"));
const FamilyTraditionBinderPage = lazy(() => import("./pages/FamilyTraditionBinderPage"));
const GrandparentsMemoryBookPage = lazy(() => import("./pages/GrandparentsMemoryBookPage"));
const HomeManagementBinderPage = lazy(() => import("./pages/HomeManagementBinderPage"));
const RecalledPage = lazy(() => import("./pages/RecalledPage"));
const EveryBoxHasAPlacePage = lazy(() => import("./pages/EveryBoxHasAPlacePage"));
const NationalParkPlannerPage = lazy(() => import("./pages/NationalParkPlannerPage"));
const PatternsPage = lazy(() => import("./pages/PatternsPage"));
const RoadTripGame1Page = lazy(() => import("./pages/RoadTripGame1Page"));
const AmericaInBloomPage = lazy(() => import("./pages/AmericaInBloomPage"));
const CommunityEventPlaybookPage = lazy(() => import("./pages/CommunityEventPlaybookPage"));
const BesideYouPage = lazy(() => import("./pages/BesideYouPage"));
const RoadTripGame2Page = lazy(() => import("./pages/RoadTripGame2Page"));
const VacationPlannerPage = lazy(() => import("./pages/VacationPlannerPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const DeleteAppPage = lazy(() => import("./pages/DeleteAppPage"));
const AffiliateDisclosurePage = lazy(() => import("./pages/AffiliateDisclosurePage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const ConsumerHealthDataPage = lazy(() => import("./pages/ConsumerHealthDataPage"));
const AiDisclosurePage = lazy(() => import("./pages/AiDisclosurePage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Admin pages (kept out of the public bundle)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const PostsList = lazy(() => import("./pages/admin/PostsList"));
const PostEditor = lazy(() => import("./pages/admin/PostEditor"));
const CategoriesManager = lazy(() => import("./pages/admin/CategoriesManager"));
const TagsManager = lazy(() => import("./pages/admin/TagsManager"));
const ContactMessages = lazy(() => import("./pages/admin/ContactMessages"));

// Neutral, layout-preserving fallback while a route chunk loads.
function RouteFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      role="status"
      aria-label="Loading"
    >
      <div className="w-8 h-8 border-4 border-[#58B88B] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Google Analytics page view tracker
function GA4PageViews() {
  const location = useLocation();
  
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);
  
  return null;
}

// Normalize trailing-slash URLs to their canonical no-trailing-slash form (except "/").
// Client-side redirect (hosting-level 301 is not repo-controllable); canonicals are
// already emitted without a trailing slash so crawlers see a single canonical URL.
function TrailingSlashRedirect() {
  const location = useLocation();
  const { pathname, search, hash } = location;
  if (pathname !== "/" && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
  }
  return null;
}

// Protected Route component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#58B88B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

// Layout wrapper that conditionally shows nav/footer
function LayoutWrapper({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) {
    return <>{children}</>;
  }
  
  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-[#2D2D3A] focus:px-4 focus:py-2 focus:text-white focus:outline-none focus:ring-2 focus:ring-white"
        data-testid="skip-to-main"
      >
        Skip to main content
      </a>
      <Navigation />
      <main id="main-content" className="flex-1 pt-32 md:pt-36">
        {children}
      </main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  return (
    <>
      <GA4PageViews />
      <TrailingSlashRedirect />
      <LayoutWrapper>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/apps/moody-meals" element={<MoodyMealsPage />} />
          <Route path="/apps/notes-from-the-nest" element={<NotesFromTheNestPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/insights" element={<Navigate to="/blog" replace />} />
          <Route path="/printables" element={<PrintablePage />} />
          <Route path="/printables/anniversary-rescue-plan" element={<Navigate to="/printables/anniversary-rescue-wife" replace />} />
          <Route path="/printables/anniversary-rescue-wife" element={<AnniversaryRescueWifePage />} />
          <Route path="/printables/anniversary-rescue-husband" element={<AnniversaryRescueHusbandPage />} />
          <Route path="/printables/anniversary-rescue-bundle" element={<AnniversaryBundlePage />} />
          <Route path="/printables/more-than-money" element={<MoreThanMoneyPage />} />
          <Route path="/printables/baby-first-year-books" element={<BabyFirstYearBooksPage />} />
          <Route path="/printables/baby-first-year-book" element={<BabyFirstYearBooksPage />} />
          <Route path="/printables/baby-survival-book" element={<BabySurvivalBookPage />} />
          <Route path="/printables/birthday-organizer" element={<BirthdayOrganizerPage />} />
          <Route path="/printables/christmas-gift-tracker" element={<ChristmasGiftTrackerPage />} />
          <Route path="/printables/christmas-managed" element={<ChristmasManagedPage />} />
          <Route path="/printables/cover-your-butt" element={<CoverYourButtPage />} />
          <Route path="/printables/cruise-planner" element={<CruisePlannerPage />} />
          <Route path="/printables/between-here-and-home" element={<BetweenHereAndHomePage />} />
          <Route path="/printables/whats-for-dinner" element={<WhatsForDinnerPage />} />
          <Route path="/printables/emergency-binder" element={<EmergencyBinderPage />} />
          <Route path="/printables/family-tradition-binder" element={<FamilyTraditionBinderPage />} />
          <Route path="/printables/grandparents-memory-book" element={<GrandparentsMemoryBookPage />} />
          <Route path="/printables/home-management-binder" element={<HomeManagementBinderPage />} />
          <Route path="/printables/recalled" element={<RecalledPage />} />
          <Route path="/printables/every-box-has-a-place" element={<EveryBoxHasAPlacePage />} />
          <Route path="/printables/national-park-planner" element={<NationalParkPlannerPage />} />
          <Route path="/printables/patterns" element={<PatternsPage />} />
          <Route path="/printables/road-trip-game-1" element={<RoadTripGame1Page />} />
          <Route path="/printables/america-in-bloom" element={<AmericaInBloomPage />} />
          <Route path="/printables/community-event-playbook" element={<CommunityEventPlaybookPage />} />
          <Route path="/printables/beside-you" element={<BesideYouPage />} />
          <Route path="/printables/road-trip-game-2" element={<RoadTripGame2Page />} />
          <Route path="/printables/vacation-planner" element={<VacationPlannerPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/delete-app" element={<DeleteAppPage />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/consumer-health-data" element={<ConsumerHealthDataPage />} />
          <Route path="/ai-disclosure" element={<AiDisclosurePage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
        
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="posts" element={<PostsList />} />
          <Route path="posts/new" element={<PostEditor />} />
          <Route path="posts/:id/edit" element={<PostEditor />} />
          <Route path="categories" element={<CategoriesManager />} />
          <Route path="tags" element={<TagsManager />} />
          <Route path="messages" element={<ContactMessages />} />
        </Route>

        {/* Branded 404 for any unknown route */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </LayoutWrapper>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen flex flex-col">
        <AuthProvider>
          <BrowserRouter>
            <ConsentProvider>
              <AppRoutes />
              <ConsentBanner />
            </ConsentProvider>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
